import React, { useEffect, useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { fetchData } from '../utills/common.utills.js';
import { SelectTimeFrame, OptTimeFrame } from '../styles/OverviewChartStyles';

const POINTS_BY_TIMEFRAME = {
  '1d': 78,
  '1w': 390,
  '1m': 1560,
};

const normalizePoints = (raw) => {
  if (!raw) return [];
  return raw.map((p) => ({
    date: p.date ?? p.Date ?? '',
    price: typeof p.price === 'number' ? p.price : Number(p.Close ?? NaN),
  })).filter((p) => p.date && Number.isFinite(p.price));
};

const OverviewChart = () => {
  const [timeframe, setTimeframe] = useState('1d');
  const [points, setPoints] = useState([]);
  const [status, setStatus] = useState({ loading: true, error: '' });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setStatus((s) => ({ ...s, loading: true, error: '' }));
        const res = await fetchData();
        const normalized = normalizePoints(res?.data);
        if (cancelled) return;

        const sorted = [...normalized].sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );
        setPoints(sorted);
      } catch (e) {
        if (!cancelled) {
          setStatus({ loading: false, error: 'Failed to load data' });
        }
      } finally {
        if (!cancelled) {
          setStatus((s) => ({ ...s, loading: false }));
        }
      }
    };

    load();
    const id = setInterval(load, 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const slicedData = useMemo(() => {
    const limit = POINTS_BY_TIMEFRAME[timeframe] ?? 78;
    return points.slice(Math.max(points.length - limit, 0));
  }, [points, timeframe]);

  return (
    <div>
      <SelectTimeFrame value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
        <OptTimeFrame value="1d">1 Day</OptTimeFrame>
        <OptTimeFrame value="1w">1 Week</OptTimeFrame>
        <OptTimeFrame value="1m">1 Month</OptTimeFrame>
      </SelectTimeFrame>

      {status.loading && <p>Loading…</p>}
      {status.error && <p style={{ color: 'tomato' }}>{status.error}</p>}

      {!status.loading && !status.error && (
        <div style={{ width: 'min(95vw, 1000px)', margin: '0 auto' }}>
          <ResponsiveContainer width="100%" aspect={2.4}>
            <LineChart data={slicedData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" minTickGap={20} />
              <YAxis domain={['auto', 'auto']} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="price" dot={false} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default OverviewChart;